import type { BrowserHistory } from 'history';
import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types';
import { createQueryStringClient } from './queryStringClient';
import { isEmpty } from 'ramda';

// TODO We would like replace this implementation with the one in combinedBrowserQueryString but removing missing filters
export const createBrowserQueryStringKeepingParams = <T>({
  passThroughParams,
  initialHistory,
}: {
  passThroughParams?: (keyof T)[];
  initialHistory?: BrowserHistory;
}): BrowserQueryString => {
  const queryStringClient = createQueryStringClient(initialHistory);

  function set(queryString: string) {
    const existingQueryString = queryStringClient.get();
    const queryParams = new URLSearchParams(existingQueryString ?? '');
    const passThroughQueryParams: Partial<T> = {};
    if (passThroughParams && !isEmpty(passThroughParams)) {
      queryParams.forEach((value, key) => {
        if (passThroughParams.includes(key as keyof T)) {
          passThroughQueryParams[key as keyof T] = value as T[keyof T];
        }
      });
    }
    const searchString = combineParams<T>(existingQueryString, queryString, passThroughQueryParams);

    queryStringClient.set(searchString);
  }

  return {
    get: queryStringClient.get,
    set: set,
  };
};

function combineParams<T>(currentParams: string, incomingParams: string, passThroughQueryParams: Partial<T>) {
  const incomingParamObject = parseParamsToObject(incomingParams);
  const currentParamsObject = parseParamsToObject(currentParams);
  const utmParams = getParamsToKeepFromCurrentParams(currentParamsObject);

  const result = { ...utmParams, ...incomingParamObject, ...passThroughQueryParams };
  const resultEntries = Object.entries(result);

  return new URLSearchParams(resultEntries).toString();
}

function parseParamsToObject(params: string) {
  const urlParams = new URLSearchParams(params);
  return Object.fromEntries(urlParams.entries());
}

function getParamsToKeepFromCurrentParams(currentParamsObject: { [key: string]: string }) {
  return Object.fromEntries(Object.entries(currentParamsObject).filter(([key]) => isParamToKeep(key)));
}

const paramsToKeep = new Set([
  'gclid',
  'gclsrc',
  'dclid',
  'wbraid',
  'gbraid',
  'fbclid',
  'fbc',
  'fbp',
  'fbtrace_id',
  'scclid',
  'pin_id',
  'ttclid',
  'li_fat_id',
  'igclid',
  'smc_upi',
  'rtbhc',
  'msclkid',
  'ranEAID',
  'ranCID',
  'ranMID',
  'ranSubID',
  'TDUID',
  'Fid',
  'cid',
  'pid',
  'tid',
  'tam',
  'agentCode',
  'nui',
  'm_i',
]);

function isParamToKeep(key: string) {
  return key.startsWith('utm_') || key.startsWith('ptm_') || paramsToKeep.has(key);
}
